import React, { useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useMount } from 'react-use';
import compact from 'lodash/compact';
import isEqual from 'lodash/isEqual';
import isUndefined from 'lodash/isUndefined';
import omitBy from 'lodash/omitBy';

import useEvent from '@lyearn/core/hooks/useEvent';
import useHotkeys from '@lyearn/core/hooks/useHotkeys';
import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { ChevronDownFill } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import {
  BaseButton,
  Box,
  InputBase,
  Loader,
  SelectedTime,
  Stack,
  Typography,
  useSnackbar,
} from '@lyearn/ui';
import { FilterOption } from '@/components/Filter/types';
import {
  FilesEntityConfig,
  PROJECTS_AND_LISTS_QUERY_PARAMS,
} from '@/components/FilterManager/EntityFilter/entity/files';
import EntityFilter from '@/components/Filters/EntityFilter';
import useUsers from '@/components/UsersSearch/hooks/useUsers';
import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';
import usePersistentState from '@/hooks/usePersistentState';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import AddTags from '@/modules/corporate/modules/tags/components/AddTags';
import AttachedTags from '@/modules/corporate/modules/tags/components/AttachedTags';
import useAttachTags from '@/modules/corporate/modules/tags/components/AttachTagsModal/hooks/useAttachTags';
import { adaptTagsOnSubmit } from '@/modules/corporate/modules/tags/utils';
import TaskAssignee from '@/modules/tasks/components/TaskAssignee';
import TaskDate from '@/modules/tasks/components/TaskDate';
import TaskStatus from '@/modules/tasks/components/TaskStatus';
import { ActionItemFragment } from '@/modules/tasks/features/actionItems/graphql/fragments/generated/ActionItem';
import { getFileIcon, getProjectIcon } from '@/modules/tasks/utils/getFileIcon';
import { getIsEditTaskDisabled } from '@/modules/tasks/views/TasksDashboard/components/TaskDetails/utils';
import {
  ActionItemPriorityEnum,
  CreateActionItemInput,
  EntityTagInput,
  InstructionInput,
  TagAvailableOnEnum,
  UpdateActionItemInput,
} from '@/types/schema';

import ActionPrioritySelector from '../../features/actionItems/components/ActionPriority/ActionPrioritySelector';
import FileEntity from '../../file.entity';
import TaskDetailsInstruction from '../../views/TasksDashboard/components/TaskDetails/components/TaskDetailsInstruction';
import { useFileQuery } from '../../views/TasksDashboard/graphql/queries/generated/file';
import { TaskDetailsFragment } from '../../views/TasksDashboard/graphql/queries/generated/taskDetails';
import getInitialTask from '../../views/TasksDashboard/utils/getInitalTasks';
import { StatusFragment } from '../../views/TaskStasusesManager/graphql/fragments/generated/Status';
import TaskLabel from '../TaskLabel';
import TaskStage from '../TaskStage';
import { baseButtonFocusStyles } from '../TaskStatus/styles';
import { fileSelectorStyles, maxW256, taskDescriptionStyles } from './styles';
import { AddOrEditTaskDialogProps } from './types';

const TEXT_INPUT_PROPS = { maxLength: 500 };

const INSTRUCTION_EDITOR_DEBOUNCED_DURATION = 100;
const INSTRUCTION_EDITOR_CLASSES = {
  container: cn('px-8', taskDescriptionStyles),
  editor: 'body-short-01 text-text-primary flex-1',
};

const AVAILABLE_ON = [TagAvailableOnEnum.Task];
const DEFAULT_LABEL_CATEGORY_ID = '625f9b3b7fa7a9d2b42e1e1c';

/**
 *
 * Some files are imported from TasksDashboard -> todo make them common
 */

export type AddOrEditTaskDialogRefType = { onClose: () => void; onSubmit: () => void };

const AddOrEditTaskDialog = React.forwardRef<AddOrEditTaskDialogRefType, AddOrEditTaskDialogProps>(
  (
    {
      onClose,
      fileId,
      task: taskToEdit,
      onCreateActionItem,
      onUpdateActionItem,
      entity,
      entityId,
      mode,
      defaultAssigneeId,
      participants,
      toggleDiscardChangesDialog,
      setFileId,
    },
    ref,
  ) => {
    const { t } = useTranslation(['Task', 'Tags']);
    const { id: userId } = useUserContext();
    const initialAssigneeId = defaultAssigneeId || userId;
    const inputRef = useRef<HTMLInputElement>(null);

    const { value: lastSelectedTaskLabelCategoryId, setValue: setLastSelectedTaskLabelCategoryId } =
      usePersistentState<string>(
        'last-selected-task-label-category-id',
        DEFAULT_LABEL_CATEGORY_ID,
        1,
      );

    useMount(() => {
      inputRef.current?.focus();
    });

    /**
     * If default values are not provided, the initial task state may change (e.g., task status is set if undefined in taskToEdit).
     * This can cause the discard dialog to appear after onClose, even if the user hasn't made any changes.
     *
     * To address this, we store updates made after the initial render in a separate state: initialDataFromSideEffect.
     * This way we prevent having multiple events writing to the same source of truth, and make the comparison for unsaved changes more reliable.
     *
     * taskToEdit: Used when editing a task or prefilling values in create mode; controlled by the consuming component.
     *
     * initialDataFromSideEffect: Stores values set as side effects after the initial render (e.g., setting task status if undefined in taskToEdit); controlled by side effects.
     *
     * taskData: The actual task data used in the form; controlled directly by user input.
     */

    const [initialDataFromSideEffect, setInitialDataFromSideEffect] = useState<
      Partial<CreateActionItemInput>
    >({});
    const initialTaskData = useMemo<CreateActionItemInput>(() => {
      const defaultTaskValues = getInitialTask({
        userId: initialAssigneeId,
        fileId,
        entity,
        entityId,
        labelCategoryId: lastSelectedTaskLabelCategoryId,
      });
      // NOTE: we do not want undefined values from taskToEdit to override default values
      // so we omit them before spreading
      const initialValuesFromProp = omitBy(taskToEdit, isUndefined) as typeof taskToEdit;
      const adaptedInitialValuesFromProp: Partial<CreateActionItemInput> = {
        ...initialValuesFromProp,
        assignedToIds: initialValuesFromProp?.assignedToIds
          ? compact(initialValuesFromProp.assignedToIds)
          : [],
        stageIds: initialValuesFromProp?.stageIds
          ? compact(initialValuesFromProp.stageIds)
          : undefined,
        tags: undefined, // these are handled separately
      };
      const task: CreateActionItemInput = {
        ...defaultTaskValues,
        ...adaptedInitialValuesFromProp,
        ...initialDataFromSideEffect,
      };
      return task;
    }, [
      entity,
      entityId,
      fileId,
      initialAssigneeId,
      initialDataFromSideEffect,
      lastSelectedTaskLabelCategoryId,
      taskToEdit,
    ]);

    const [taskData, setTaskData] = useState(initialTaskData);

    const parentId = taskData.parentId;

    const disabled =
      mode === 'edit' && taskToEdit
        ? getIsEditTaskDisabled(taskToEdit as unknown as TaskDetailsFragment)
        : false;

    const [{ data, fetching }] = useFileQuery({
      variables: {
        filters: {
          id: taskData?.fileId!,
        },
      },
      pause: !taskData?.fileId,
    });

    const [isFileSelectorOpen, { toggle: toggleFileSelector }] = useToggle(false);

    const onModalClose = useEvent(() => {
      // TODO: @aryan02420 - does not account for changes to tags in create mode
      if (isEqual(Object.values(initialTaskData), Object.values(taskData))) {
        onClose();
      } else {
        toggleDiscardChangesDialog();
      }
    });

    const { data: allAssignedUsers } = useUsers({
      ids: (taskData.assignedToIds || []).filter(Boolean) as string[],
      pause: !taskData.assignedToIds?.length,
    });

    const assignedUsers =
      taskData?.assignedToIds?.length && taskData.assignedToIds.length > 0 ? allAssignedUsers : [];

    const { showErrorSnackbar } = useSnackbar();

    const selectedFileData = data?.file;
    const onFilesChange = useCallback(
      (value: FilterOption[]) => {
        setTaskData((prevTaskData) => ({
          ...prevTaskData,
          fileId: value[0].value!,
          stageIds: undefined,
        }));
        setFileId(value[0].value);
      },
      [setFileId],
    );

    const renderSelectedFile = () => {
      if (fetching) {
        return (
          <Stack alignItems="center" className="h-22" justifyContent="center">
            <Loader />
          </Stack>
        );
      }

      return (
        <Stack alignItems="center" className="group">
          {selectedFileData
            ? getFileIcon(
                selectedFileData,
                20,
                cn({
                  'text-icon-primary': isFileSelectorOpen,
                  'text-icon-secondary group-hover:text-icon-primary': !isFileSelectorOpen,
                }),
              )
            : getProjectIcon(20, 'text-icon-placeholder')}
          <Typography
            className={cn('ms-8 me-4', {
              'text-text-primary': isFileSelectorOpen,
              'text-text-secondary focus:text-text-primary group-hover:text-text-paragraph':
                !isFileSelectorOpen,
            })}
            variant="body-short-02">
            {selectedFileData?.name}
          </Typography>
          <ChevronDownFill
            className={cn('h-16 w-16', {
              'text-icon-primary': isFileSelectorOpen,
              'text-icon-secondary group-hover:text-icon-primary': !isFileSelectorOpen,
            })}
          />
        </Stack>
      );
    };

    const onTaskInstructionChange = useCallback((instruction: InstructionInput) => {
      setTaskData((prevTaskData) => ({
        ...prevTaskData,
        instruction: {
          ...instruction,
        },
      }));
    }, []);

    const onTaskTitleChange = useCallback((title: string) => {
      setTaskData((prevTaskData) => ({
        ...prevTaskData,
        title,
      }));
    }, []);

    const onTaskStatusChange = useCallback(
      (statusInfo: StatusFragment, id?: string, isSideEffect?: boolean) => {
        setTaskData((prevTaskData) => {
          return {
            ...prevTaskData,
            statusId: statusInfo.id,
            status: statusInfo.name,
            category: statusInfo.category,
          };
        });
        if (isSideEffect) {
          setInitialDataFromSideEffect((prev) => ({
            ...prev,
            statusId: statusInfo.id,
            status: statusInfo.name,
            category: statusInfo.category,
          }));
        }
      },
      [],
    );

    const onTaskLabelChange = useCallback(
      (labelCategory: any, id?: string) => {
        setTaskData((prevTaskData) => {
          return {
            ...prevTaskData,
            labelCategoryId: labelCategory.id,
          };
        });
        setLastSelectedTaskLabelCategoryId(labelCategory.id);
      },
      [setLastSelectedTaskLabelCategoryId],
    );

    const onTaskPriorityChange = useCallback((priority: ActionItemPriorityEnum) => {
      setTaskData((prevTaskData) => ({
        ...prevTaskData,
        priority,
      }));
    }, []);

    const onTaskAssigneeChange = useCallback((assignedToIds: string[]) => {
      setTaskData((prevTaskData) => ({
        ...prevTaskData,
        assignedToIds,
      }));
    }, []);

    const onTaskDateChange = useCallback((dueDate?: SelectedTime) => {
      setTaskData((prevTaskData) => ({
        ...prevTaskData,
        dueDate: dueDate?.toISOString(),
      }));
    }, []);

    const onTaskStageChange = useCallback((stageId?: string) => {
      setTaskData((prevTaskData) => ({
        ...prevTaskData,
        stageIds: stageId ? [stageId] : undefined,
      }));
    }, []);

    const {
      tag,
      onTagChange,
      onTagValueChange,
      validate,
      error: tagError,
      onRemoveTag,
    } = useAttachTags({
      tags: taskToEdit?.tags as EntityTagInput[],
      availableOn: AVAILABLE_ON,
      hasError: false,
      subWorkspaceIds: [],
    });

    const onSubmit = useCallback(() => {
      if (!taskData.title.trim()) {
        showErrorSnackbar({ message: t('Task:EmptyTitleError') });
        return;
      }

      {
        /* DISABLED: Tags are not required for tasks. */
      }
      // if (validate(tag) === false) {
      //   showErrorSnackbar({ message: t(REQUIRED_TAGS_EMPTY_ERROR) });
      //   return;
      // }

      if (mode === 'create') {
        onCreateActionItem?.({
          ...taskData,
          assignedToIds: (taskData.assignedToIds || []).filter(Boolean) as string[],
          tags: adaptTagsOnSubmit(tag),
          fileId: taskData.fileId,
          entity: entity && entityId ? { entityType: entity, entityId } : undefined,
          stageIds: taskData.stageIds?.filter(Boolean) as string[],
        });
        return;
      }

      if (mode === 'edit' && taskToEdit) {
        const taskToUpdate: UpdateActionItemInput = {
          tags: adaptTagsOnSubmit(tag),
          actionItemId: (taskToEdit as ActionItemFragment).id,
          assignedToIds: {
            set: (taskData.assignedToIds || []).filter(Boolean) as string[],
          },
          dueDate: taskData.dueDate,
          fileId: taskData.fileId,
          instruction: taskData.instruction,
          metadata: taskData.metadata,
          priority: taskData.priority,
          title: taskData.title,
          statusId: taskData.statusId,
          status: taskData.status,
          category: taskData.category,
          labelCategoryId: taskData.labelCategoryId,
          stageIds: {
            set: taskData.stageIds?.filter(Boolean) as string[],
          },
        };

        onUpdateActionItem?.(taskToUpdate);
        return;
      }
    }, [
      entity,
      entityId,
      mode,
      onCreateActionItem,
      onUpdateActionItem,
      showErrorSnackbar,
      t,
      tag,
      taskData,
      taskToEdit,
    ]);

    useHotkeys(
      'meta+enter',
      onSubmit,
      {
        enableOnFormTags: true,
        enableOnContentEditable: true,
      },
      [onSubmit],
    );

    useImperativeHandle(ref, () => ({ onClose: onModalClose, onSubmit }), [onModalClose, onSubmit]);

    const taskPillProps = { className: cn('ps-8 pe-12 h-32', maxW256), variant: 'modal' as const };

    const {
      [FeatureFlags.EnableProjectManagementPreReleaseFeatures]:
        isProjectManagementPreReleaseEnabled,
    } = useFeatureFlags();

    return (
      <Stack className="border-b border-divider p-16 pt-20" direction="col">
        <EntityFilter
          initialSelectedOptions={taskData?.fileId ? [taskData.fileId] : []}
          listConfig={{
            ...FilesEntityConfig,
            listVariant: 'single-ungrouped-infinite',
          }}
          open={isFileSelectorOpen}
          queryParams={PROJECTS_AND_LISTS_QUERY_PARAMS}
          uiConfig={{
            searchPlaceholder: t('Common:Filter.SearchList'),
          }}
          onChange={onFilesChange}
          onToggle={parentId ? undefined : toggleFileSelector}>
          <BaseButton
            className={cn(
              baseButtonFocusStyles,
              'mb-10 h-32 w-max py-0 px-8',
              fileSelectorStyles,
              parentId ? 'cursor-not-allowed' : 'cursor-pointer',
            )}
            disabled={!!parentId}>
            {renderSelectedFile()}
          </BaseButton>
        </EntityFilter>
        <InputBase
          autoFocus
          multiline
          className="heading-m px-8"
          inputProps={TEXT_INPUT_PROPS}
          inputRef={inputRef}
          minRows={1}
          placeholder={t('Task:TaskTitle')}
          value={taskData.title}
          onChange={(e) => onTaskTitleChange(e.currentTarget.value)}
        />
        {disabled ? (
          <Box className={taskDescriptionStyles} />
        ) : (
          <TaskDetailsInstruction
            classes={INSTRUCTION_EDITOR_CLASSES}
            debouncedDuration={INSTRUCTION_EDITOR_DEBOUNCED_DURATION}
            value={taskData.instruction as ActionItemFragment['instruction']}
            onChange={onTaskInstructionChange}
          />
        )}
        <Stack alignItems="center" className="px-4 pt-12 flex-wrap" gap={8} justifyContent="start">
          <TaskLabel
            editable={!disabled}
            labelCategoryId={taskData.labelCategoryId}
            onChange={onTaskLabelChange}
            {...taskPillProps}
          />
          <TaskStatus
            editable={!disabled}
            statusId={taskData.statusId}
            onChange={onTaskStatusChange}
            {...taskPillProps}
          />
          <ActionPrioritySelector
            editable={!disabled}
            priority={taskData.priority ?? undefined}
            onChange={onTaskPriorityChange}
            {...taskPillProps}
          />
          <TaskAssignee
            editable
            participants={participants}
            placeholder={t('Task:Unassigned')}
            users={assignedUsers}
            onChange={onTaskAssigneeChange}
            {...taskPillProps}
          />
          <TaskDate
            editable
            date={taskData.dueDate}
            placeholder={t('Task:DueDate')}
            onChange={onTaskDateChange}
            {...taskPillProps}
          />
          {selectedFileData &&
          FileEntity.shouldShowStages(selectedFileData) &&
          isProjectManagementPreReleaseEnabled ? (
            <TaskStage
              editable
              placeholder={t('Task:Stage')}
              projectId={selectedFileData.id}
              stageId={taskData.stageIds?.[0]}
              onChange={onTaskStageChange}
              {...taskPillProps}
            />
          ) : null}
          <AttachedTags
            className="contents"
            error={tagError}
            itemClassName="!h-32 !px-8 !w-fit"
            selectedTagValues={tag?.tagValues}
            tags={tag?.tags}
            onRemoveTag={onRemoveTag}
            onTagsValueChange={onTagValueChange}
          />
        </Stack>
        <AddTags
          availableOn={AVAILABLE_ON}
          className="!h-32 mt-8 mx-4"
          tags={tag?.tags}
          onChange={onTagChange}
        />
      </Stack>
    );
  },
);

AddOrEditTaskDialog.displayName = 'AddOrEditTaskDialog';

export default AddOrEditTaskDialog;
